import React, { useState } from "react";
import toast from "react-hot-toast";
import axios from "../../../Utils/axiosInstance";
import FloatInput from "../../Common/FloatInput";

const BUSINESS_TYPE_OPTIONS = [
  "Proprietorship",
  "Partnership",
  "LLP",
  "Pvt. Ltd.",
  "OPC Pvt. Ltd.",
  "Limited Company",
  "FPO/FPC",
  "Cooperative Society",
  "Other",
];

const BusinessTypeSelect = ({ value, onChange }) => {
  // Track whether user explicitly picked "Other"
  const [showCustom, setShowCustom] = useState(
    value !== "" && !BUSINESS_TYPE_OPTIONS.slice(0, -1).includes(value),
  );

  const selectValue = showCustom ? "Other" : value;

  const handleSelectChange = (e) => {
    if (e.target.value === "Other") {
      setShowCustom(true);
      onChange(""); // clear parent value so user types fresh
    } else {
      setShowCustom(false);
      onChange(e.target.value);
    }
  };

  return (
    <div className="space-y-2">
      {/* Dropdown */}
      <div className="relative">
        <select
          value={selectValue}
          onChange={handleSelectChange}
          className={`
            w-full px-4 py-3.5 border-2 rounded-xl outline-none text-sm bg-white
            appearance-none transition-all duration-200
            ${selectValue ? "text-gray-800" : "text-gray-400"}
            focus:border-primaryColor focus:shadow-sm focus:shadow-primaryColor/20
            border-gray-200
          `}
        >
          <option value="" disabled>
            Business Type *
          </option>
          {BUSINESS_TYPE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>

        {/* Custom caret */}
        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
          <svg
            className="w-4 h-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {/* Custom input — shown only when "Other" is selected */}
      {showCustom && (
        <input
          type="text"
          placeholder="Enter your business type *"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
          className="
            w-full px-4 py-3.5 border-2 rounded-xl outline-none text-sm text-gray-800 bg-white
            border-primaryColor shadow-sm shadow-primaryColor/20
            transition-all duration-200
          "
        />
      )}
    </div>
  );
};

const RegisterDetailsStep = ({
  regData,
  setRegData,
  selectedRole = "buyer",
  hasGst = "YES",
  setHasGst,
  aadhaarNumber = "",
  setAadhaarNumber,
  isAadharVerified = false,
  setIsAadharVerified,
  aadhaarDetails = null,
  setAadhaarDetails,
  gstInfo,
  gstError,
  gstLoading,
  onVerifyGST,
  onContinue,
}) => {
  const [isInitializingDigilocker, setIsInitializingDigilocker] =
    useState(false);
  const [isPollingStatus, setIsPollingStatus] = useState(false);

  // ── DigiLocker Aadhaar Verification ──────────────────────────
  const handleVerifyAadharViaDigilocker = async () => {
    const cleanAadhar = String(aadhaarNumber || "").replace(/[^0-9]/g, "");
    if (cleanAadhar.length !== 12) {
      return toast.error("Please enter a valid 12-digit Aadhaar Card Number");
    }

    const uniqueId = `v_id_${Date.now()}_${Math.floor(
      1000 + Math.random() * 9000,
    )}`;

    setIsInitializingDigilocker(true);

    try {
      // 1. Check DigiLocker account and link Aadhaar
      await axios.post("/user/digilocker/verify-account", {
        verificationId: uniqueId,
        aadhaarNumber: cleanAadhar,
      });

      // 2. Create DigiLocker Consent URL (Cashfree requires redirectUrl to start with https://)
      const callbackUrl = window.location.origin.startsWith("https://")
        ? `${window.location.origin}/digilocker-callback`
        : "https://pcm.exports/digilocker-callback";
      const response = await axios.post("/user/digilocker/create-url", {
        verificationId: uniqueId,
        redirectUrl: callbackUrl,
        userFlow: "signup",
      });

      if (response.data?.success && response.data.data?.url) {
        const url = response.data.data.url;
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        const popup = window.open(
          url,
          "DigiLocker Verification",
          `width=${width},height=${height},top=${top},left=${left}`,
        );

        startPollingDigilocker(uniqueId, popup);
      } else {
        toast.error(
          response.data?.message ||
            "Failed to initialize DigiLocker verification",
        );
      }
    } catch (error) {
      console.error("DigiLocker verification error:", error);
      toast.error(
        error?.response?.data?.message ||
          "An error occurred while starting DigiLocker verification",
      );
    } finally {
      setIsInitializingDigilocker(false);
    }
  };

  const startPollingDigilocker = (verId, popup) => {
    let attempts = 0;
    const maxAttempts = 30;
    const intervalTime = 3000;
    setIsPollingStatus(true);

    const closePopupSafely = () => {
      try {
        if (popup && !popup.closed) {
          popup.close();
        }
      } catch (err) {
        console.log("Could not close popup directly:", err);
      }
    };

    // Also listen for postMessage from digilocker-callback
    const handleMessage = (event) => {
      if (event.data?.type === "DIGILOCKER_CALLBACK_COMPLETE") {
        closePopupSafely();
      }
    };
    window.addEventListener("message", handleMessage);

    const poll = setInterval(async () => {
      attempts++;

      try {
        const response = await axios.get("/user/digilocker/status", {
          params: { verificationId: verId },
        });

        if (response.data?.success && response.data.data) {
          const { status } = response.data.data;

          if (status === "AUTHENTICATED") {
            clearInterval(poll);
            window.removeEventListener("message", handleMessage);
            setIsPollingStatus(false);
            closePopupSafely();
            await fetchAadharDocument(verId);
          } else if (status === "CONSENT_DENIED" || status === "EXPIRED") {
            clearInterval(poll);
            window.removeEventListener("message", handleMessage);
            setIsPollingStatus(false);
            closePopupSafely();
            toast.error(`DigiLocker verification status: ${status}`);
          }
        }
      } catch (error) {
        console.error("Error polling DigiLocker status:", error);
      }

      if (attempts >= maxAttempts) {
        clearInterval(poll);
        window.removeEventListener("message", handleMessage);
        setIsPollingStatus(false);
        toast.error("Aadhaar verification timed out. Please try again.");
      }
    }, intervalTime);
  };

  const fetchAadharDocument = async (verId) => {
    try {
      const response = await axios.get(
        "/user/digilocker/document/AADHAAR",
        { params: { verificationId: verId } },
      );

      if (response.data?.success && response.data.data) {
        const rawDoc = response.data.data?.data || response.data.data;
        const fullName = rawDoc.name || rawDoc.fullName || "";
        const normalizedDoc = {
          ...rawDoc,
          fullName: fullName,
          name: fullName,
          dob: rawDoc.dob || "",
          gender: rawDoc.gender || "",
        };

        setIsAadharVerified(true);
        setAadhaarDetails(normalizedDoc);

        // Autofill first & last name if not entered yet
        if (fullName) {
          const parts = fullName.trim().split(" ");
          setRegData((prev) => ({
            ...prev,
            firstName: prev.firstName || parts[0] || "",
            lastName:
              prev.lastName || (parts.length > 1 ? parts.slice(1).join(" ") : ""),
          }));
        }

        toast.success("Aadhaar verified successfully via DigiLocker!");
      } else {
        toast.error(
          response.data?.message || "Failed to retrieve Aadhaar details",
        );
        setAadhaarDetails(null);
      }
    } catch (error) {
      console.error("Fetch Aadhaar Document error:", error);
      toast.error(
        error?.response?.data?.message ||
          "An error occurred while fetching your Aadhaar document",
      );
      setAadhaarDetails(null);
    }
  };

  const isSupplierNoGst = selectedRole === "supplier" && hasGst === "NO";

  return (
    <div className="h-full md:h-[35rem] overflow-y-scroll scroll-bar">
      <div className="flex pb-6 md:pt-6 flex-col flex-1 justify-between gap-4 pr-1">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-primaryColor">
              Register as{" "}
              <span className="capitalize">
                {selectedRole === "supplier" ? "Supplier" : "Buyer"}
              </span>
            </h1>
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-primaryColor/10 text-primaryColor capitalize">
              {selectedRole} Account
            </span>
          </div>

          {/* Company Name */}
          <FloatInput
            label="Company Name"
            required
            value={regData.companyName || ""}
            onChange={(e) =>
              setRegData({ ...regData, companyName: e.target.value })
            }
          />

          {/* Business Type */}
          <BusinessTypeSelect
            value={regData.businessType || ""}
            onChange={(val) => setRegData({ ...regData, businessType: val })}
          />

          {/* Designation */}
          <FloatInput
            label="Designation"
            required
            value={regData.designation || ""}
            onChange={(e) =>
              setRegData({ ...regData, designation: e.target.value })
            }
          />
          <hr className="my-2" />

          {/* Legal Representative group label */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">
              Legal Representative
            </p>
            <div className="space-y-3">
              {/* First Name + Middle Name */}
              <div className="grid grid-cols-2 gap-3">
                <FloatInput
                  label="First Name"
                  required
                  value={regData.firstName}
                  onChange={(e) =>
                    setRegData({ ...regData, firstName: e.target.value })
                  }
                />
                <FloatInput
                  label="Middle Name"
                  value={regData.middleName || ""}
                  onChange={(e) =>
                    setRegData({ ...regData, middleName: e.target.value })
                  }
                />
              </div>
              {/* Last Name */}
              <FloatInput
                label="Last Name"
                required
                value={regData.lastName}
                onChange={(e) =>
                  setRegData({ ...regData, lastName: e.target.value })
                }
              />
            </div>
          </div>
          <hr className="my-2" />
          <FloatInput
            label="Email"
            type="email"
            required
            value={regData.email}
            onChange={(e) => setRegData({ ...regData, email: e.target.value })}
            disabled={!!regData.isPartnerAutofilled}
          />

          {/* IEC Number */}
          <div className="relative">
            <FloatInput
              label="IEC Number (10-digit alphanumeric)"
              maxLength={10}
              value={regData.IEC || ""}
              onChange={(e) => {
                const val = e.target.value
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, "");
                setRegData({ ...regData, IEC: val });
              }}
            />
            {regData.IEC && regData.IEC.length > 0 && (
              <span
                className={`absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${regData.IEC.length === 10 ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"}`}
              >
                {regData.IEC.length}/10
              </span>
            )}
          </div>

          {/* ── Supplier GST Options: YES / NO ── */}
          {selectedRole === "supplier" && (
            <div className="space-y-2 p-3 bg-gray-50 border border-gray-200/80 rounded-2xl">
              <label className="text-xs font-semibold text-gray-700 block">
                Do you have GST? <span className="text-primaryColor">*</span>
              </label>
              <div className="flex gap-5">
                {["YES", "NO"].map((option) => (
                  <label
                    key={option}
                    onClick={() => setHasGst(option)}
                    className="flex items-center gap-2 cursor-pointer select-none"
                  >
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        hasGst === option
                          ? "border-primaryColor bg-white"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {hasGst === option && (
                        <div className="w-2.5 h-2.5 rounded-full bg-primaryColor" />
                      )}
                    </div>
                    <span
                      className={`text-xs font-semibold ${
                        hasGst === option
                          ? "text-primaryColor"
                          : "text-gray-600"
                      }`}
                    >
                      {option}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* ── GST Field (For Buyer or Supplier with GST) ── */}
          {!isSupplierNoGst && (
            <div>
              <div className="relative">
                <FloatInput
                  label="GST Number (15 digits)"
                  required
                  maxLength={15}
                  value={regData.GST}
                  onChange={(e) => {
                    const val = e.target.value.toUpperCase();
                    setRegData({ ...regData, GST: val });
                    if (val.length === 15) onVerifyGST(val);
                  }}
                />
                {/* GST char counter badge */}
                {regData.GST.length > 0 && (
                  <span
                    className={`absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${regData.GST.length === 15 ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"}`}
                  >
                    {regData.GST.length}/15
                  </span>
                )}
              </div>

              {gstLoading && (
                <div className="flex items-center gap-2 mt-2">
                  <svg
                    className="animate-spin w-3.5 h-3.5 text-blue-500"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                  <p className="text-xs text-blue-600">Verifying GST...</p>
                </div>
              )}

              {gstError && (
                <div className="flex items-center gap-2 mt-2">
                  <svg
                    className="w-3.5 h-3.5 text-red-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-xs text-red-600">{gstError}</p>
                </div>
              )}

              {gstInfo && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <svg
                      className="w-4 h-4 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>

                    <p className="font-semibold text-green-700 text-xs">
                      GST Verified
                    </p>
                  </div>

                  <p className="font-semibold text-gray-800">
                    {gstInfo.trade_name_of_business ||
                      gstInfo.legal_name_of_business}
                  </p>

                  <p className="text-xs text-gray-600 mt-1">
                    {gstInfo.principal_place_address}
                  </p>

                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                    <div>
                      <span className="font-medium">GST Status:</span>
                      <br />
                      {gstInfo.gst_in_status}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Supplier NO GST Notice + Aadhaar Card Section ── */}
          {isSupplierNoGst && (
            <div className="space-y-3">
              {/* Notice */}
              <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200/80 rounded-xl">
                <svg
                  className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-xs text-amber-800 leading-relaxed">
                  Since you don't have a GST number, your account will be
                  manually reviewed by our admin after registration.
                </p>
              </div>

              {/* Aadhaar Input */}
              <div className="relative">
                <FloatInput
                  label="Aadhaar Card Number (12 digits)"
                  required
                  maxLength={12}
                  disabled={isAadharVerified}
                  value={aadhaarNumber || ""}
                  onChange={(e) => {
                    const digits = e.target.value
                      .replace(/[^0-9]/g, "")
                      .slice(0, 12);
                    setAadhaarNumber(digits);
                  }}
                />

                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  {isAadharVerified ? (
                    <span className="flex items-center gap-1 text-[11px] font-bold text-green-700 bg-green-100 px-2.5 py-1 rounded-full border border-green-200">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Verified
                    </span>
                  ) : String(aadhaarNumber || "").replace(/\D/g, "")
                      .length === 12 ? (
                    <button
                      type="button"
                      onClick={handleVerifyAadharViaDigilocker}
                      disabled={isInitializingDigilocker || isPollingStatus}
                      className="text-xs font-semibold bg-primaryColor text-white px-3 py-1.5 rounded-lg shadow-sm hover:bg-primaryColor/90 transition-all disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {isInitializingDigilocker || isPollingStatus ? (
                        <>
                          <svg
                            className="animate-spin w-3 h-3"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            />
                          </svg>
                          {isPollingStatus ? "Verifying..." : "Starting..."}
                        </>
                      ) : (
                        "Verify via DigiLocker"
                      )}
                    </button>
                  ) : String(aadhaarNumber || "").length > 0 ? (
                    <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                      {String(aadhaarNumber || "").length}/12
                    </span>
                  ) : null}
                </div>
              </div>

              {isAadharVerified && aadhaarDetails && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-xs space-y-1">
                  <p className="font-semibold text-green-800 flex items-center gap-1">
                    <svg
                      className="w-4 h-4 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Aadhaar Document Verified via DigiLocker
                  </p>
                  {aadhaarDetails.fullName && (
                    <p className="text-gray-700">
                      <span className="font-medium">Name:</span>{" "}
                      {aadhaarDetails.fullName}
                    </p>
                  )}
                  {aadhaarDetails.dob && (
                    <p className="text-gray-700">
                      <span className="font-medium">DOB:</span>{" "}
                      {aadhaarDetails.dob}
                    </p>
                  )}
                  {aadhaarDetails.gender && (
                    <p className="text-gray-700">
                      <span className="font-medium">Gender:</span>{" "}
                      {aadhaarDetails.gender}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Referral Code (Optional) */}
          <FloatInput
            label="Referral Code (Optional)"
            value={regData.referralCode || ""}
            onChange={(e) =>
              setRegData({ ...regData, referralCode: e.target.value })
            }
            placeholder="Enter referral code if any"
          />
        </div>

        <div className="space-y-3">
          {/* Data safety notice */}
          <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-700">
                Your data is safe with PCM Exports
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">
                All information shared during registration is securely stored
                and used only for verification and transaction purposes.
              </p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <button
          onClick={onContinue}
          className="sticky bottom-0 z-20
          flex-1 bg-primaryColor w-full h-fit text-white py-3.5 rounded-xl
          font-semibold text-sm hover:bg-primaryColor/90
          transition-all duration-200 active:scale-[0.98]
          flex items-center justify-center gap-2 shadow-md shadow-primaryColor/30
        "
        >
          Continue to E-Signature
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13 7l5 5-5 5M6 12h12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default RegisterDetailsStep;
